
import { Component, inject, linkedSignal } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { environment } from '../../../../environments/environment';
import { LanguageHelperService } from '../../../services/language.service';
import { APP_ICONS } from '../../../common/constants';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { TranslatePipe } from '@ngx-translate/core';

/**
 * This component handles language switching.
 */
@Component({
    selector: 'app-language-modal',
    templateUrl: './language-modal.component.html',
    styleUrls: ['./language-modal.component.scss'],
    imports: [MatIconModule, MatMenuModule, TranslatePipe],
    standalone: true,
    providers: []
})
export class LanguageModalComponent {
  // Inject MatDialogRef.
  private readonly dialogRef = inject<MatDialogRef<LanguageModalComponent>>(MatDialogRef);
  // Inject LanguageHelperService.
  readonly languageHelperService = inject(LanguageHelperService);

  // Available languages.
  readonly availableLanguages = environment.availableLanguages;
  // The language selected in the modal. Follows the app language until the user picks another one.
  readonly currentLanguage = linkedSignal<string | undefined>(() => {
    const appLanguage = this.languageHelperService.currentLanguage();
    return this.availableLanguages.find((lang) => lang === appLanguage);
  });

  /**
   * Select current language.
   *
   * @param language Represents the language.
   */
  selectLanguage(language: string): void {
    this.currentLanguage.set(language);
  }

  /**
   * Closes the modal.
   */
  close(): void {
    this.dialogRef.close();
  }

  /**
   * Applies current language and translations to whole app.
   * Also saves the selected language for consistency.
   */
  save(): void {
    const language = this.currentLanguage();
    if (language) {
      this.languageHelperService.setLanguage(language, true);
      this.dialogRef.close(true);
    }
  }

  /**
   * Check if flag icon svg exists.
   *
   * @param language Represents the selected language.
   *
   * @returns True if the flag exists as a svg, false otherwise.
   */
  checkFlagIcon(language: string): boolean {
    const findFlag = APP_ICONS.find((el) => el === 'flags/' + language);
    if (!findFlag) {
      return false;
    }
    return true;
  }
}
